import { useContext, useEffect, useRef } from 'react';

import observer from 'saddlebag-observer';

import type {
  GetHotelDetailPriceRequest,
  GetHotelDetailPriceResponse,
} from '@shared/types/frontend/ubff/hotel-detail-price';
import type { FrisbeeClientError } from '@shared/types/frontend/ubff-gateway';
import type { Nullable } from '@shared/types/utils';

import {
  UBFF_PRICES_EMPTY,
  UBFF_PRICES_FAILURE,
  UBFF_PRICES_START,
  UBFF_PRICES_SUCCESS,
} from '@shared/constants/newrelic-events/event-names';
import UserContext from '@shared/utils-frontend/contexts/UserContext';
import logger from '@shared/utils-frontend/logger';
import { getSource } from '@shared/utils-universal/traffic-source';

export type UseHotelOffersApiMetricsProps = {
  request: GetHotelDetailPriceRequest;
  response: Nullable<GetHotelDetailPriceResponse>;
  error: Nullable<FrisbeeClientError>;
};

const useHotelOffersApiMetrics = ({
  error,
  request,
  response,
}: UseHotelOffersApiMetricsProps) => {
  const userContext = useContext(UserContext);

  const eventDataRef = useRef({
    trafficSource: getSource(),
    isRobot: `${!!userContext.device.isRobot}`,
    market: request.travellerContext?.market || '',
    isFiltered: `${!!request.filters?.length}`,
  });
  const startTime = useRef(0);

  const publishPricesLoadedEvent = () => {
    observer.publish('hotel-data-result-is-loaded', {
      type: 'prices',
    });
  };

  useEffect(() => {
    eventDataRef.current = {
      ...eventDataRef.current,
      isFiltered: `${!!request.filters?.length}`,
    };
    logger.event(UBFF_PRICES_START, eventDataRef.current);

    startTime.current = Date.now();
  }, [request]);

  useEffect(() => {
    if (!response || response.pollingStatus !== 'SEARCH_STATUS_COMPLETED') {
      return;
    }
    const { partnerOffers } = response;
    publishPricesLoadedEvent();

    const availableOffers = partnerOffers && partnerOffers.length > 0;

    if (availableOffers) {
      const timeTaken = Date.now() - startTime.current;
      logger.event(UBFF_PRICES_SUCCESS, {
        ...eventDataRef.current,
        duration: timeTaken,
      });
    } else if (!availableOffers) {
      logger.event(UBFF_PRICES_EMPTY, eventDataRef.current);
    }
  }, [response]);

  useEffect(() => {
    if (error) {
      logger.event(UBFF_PRICES_FAILURE, {
        ...eventDataRef.current,
        errorCode: error?.code,
        errorMessage: error?.message,
      });
    }
  }, [error]);
};

export { useHotelOffersApiMetrics };
